import { Reorder } from "framer-motion";
import { PencilSimple, Trash, DotsSixVertical } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { optimizeImageUrl } from "@/utils/imageOptimizer";

// Categories are dynamic now — parent passes them in. Falls back to
// the union of item.category values so the reorder view still renders
// on a legacy install that predates the /menu-categories endpoint.
const _titleCase = (name) =>
  (name || "").replace(/\b\w/g, (c) => c.toUpperCase());

const sortItems = (items) => {
  // Explicit sort_order (0, 1, 2, …) sits above unsorted (alphabetical).
  return [...items].sort((a, b) => {
    const ao = a.sort_order ?? Number.POSITIVE_INFINITY;
    const bo = b.sort_order ?? Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    return (a.name || "").localeCompare(b.name || "");
  });
};

const MenuItemsReorder = ({
  items,
  categories,          // [{name, sort_order}, ...] from /api/menu-categories
  onReorder,
  onEdit,
  onDelete,
  onToggleAvailability,
  cashDiscountPercentage = 0,
}) => {
  const catList = (() => {
    if (categories && categories.length > 0) {
      return categories.map((c) => ({ id: c.name, label: _titleCase(c.name) }));
    }
    // Fallback so the admin reorder view still shows *something* when
    // the categories endpoint hasn't returned yet.
    const seen = new Set();
    const rows = [];
    items.forEach((it) => {
      if (it.category && !seen.has(it.category)) {
        seen.add(it.category);
        rows.push({ id: it.category, label: _titleCase(it.category) });
      }
    });
    return rows;
  })();
  const byCategory = catList.map((cat) => ({
    ...cat,
    items: sortItems(items.filter((it) => it.category === cat.id)),
  }));
  // "Card price" preview lets the operator eyeball what non-cash guests
  // will actually pay if they flip on Dual Pricing. We render it inline
  // next to the sticker price so no page-flip is needed. Only shows when
  // a surcharge percentage is configured (>0), regardless of whether the
  // toggle is currently on — the goal is preview *before* switching it on.
  const showCardPrice = Number(cashDiscountPercentage) > 0;
  const priceWithSurcharge = (base) =>
    Math.round(base * (1 + Number(cashDiscountPercentage) / 100) * 100) / 100;

  return (
    <div className="space-y-8" data-testid="menu-reorder-list">
      {byCategory.map((cat) => (
        <div key={cat.id} data-testid={`menu-category-${cat.id}`}>
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">{cat.label}</h3>
            <span className="text-xs text-muted-foreground">{cat.items.length} item{cat.items.length === 1 ? "" : "s"}</span>
          </div>
          {cat.items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic px-4 py-3 border border-dashed border-border rounded-lg">
              No items in this category yet.
            </p>
          ) : (
            <Reorder.Group
              axis="y"
              values={cat.items}
              onReorder={(newOrder) => onReorder(cat.id, newOrder.map((it) => it.id))}
              className="space-y-2"
            >
              {cat.items.map((item) => (
                <Reorder.Item
                  key={item.id}
                  value={item}
                  className={`rounded-lg p-4 flex items-center justify-between cursor-grab active:cursor-grabbing ${item.available ? "bg-secondary/50" : "bg-secondary/20 opacity-60"}`}
                  data-testid={`menu-item-${item.id}`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <DotsSixVertical size={20} className="text-muted-foreground shrink-0" data-testid={`menu-drag-${item.id}`} />
                    {item.image_url && (
                      <img
                        src={optimizeImageUrl(item.image_url, 60)}
                        alt={item.name}
                        width={48}
                        height={48}
                        loading="lazy"
                        decoding="async"
                        className="w-12 h-12 object-cover rounded-lg shrink-0"
                      />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium truncate">{item.name}</p>
                      <p className="text-sm text-muted-foreground">
                        <span data-testid={`menu-cash-price-${item.id}`}>${item.price.toFixed(2)}</span>
                        {showCardPrice && (
                          <>
                            <span className="mx-1.5 text-muted-foreground/40">·</span>
                            <span
                              className="text-emerald-700 font-medium"
                              data-testid={`menu-card-price-${item.id}`}
                              title={`Card price = cash × (1 + ${cashDiscountPercentage}%)`}
                            >
                              ${priceWithSurcharge(item.price).toFixed(2)}
                            </span>
                            <span className="text-[10px] text-muted-foreground/70 ml-1">
                              (card)
                            </span>
                          </>
                        )}
                      </p>
                      {(item.modifier_groups?.length > 0 || item.modifiers?.length > 0) && (
                        <p className="text-xs text-accent">{item.modifier_groups?.length || 0} modifier group(s)</p>
                      )}
                      {item.location_prices?.length > 0 && (
                        <p className="text-xs text-blue-600">{item.location_prices.length} location price(s)</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">Available</Label>
                      <Switch
                        checked={item.available}
                        onCheckedChange={() => onToggleAvailability(item)}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      data-testid={`edit-menu-${item.id}`}
                    >
                      <PencilSimple size={16} className="mr-1" /> Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      className="text-destructive"
                      data-testid={`delete-menu-${item.id}`}
                    >
                      <Trash size={18} />
                    </Button>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          )}
        </div>
      ))}
    </div>
  );
};

export default MenuItemsReorder;
